/* Green Gorilla Fruits — cart drawer
 * --------------------------------------------------------------
 * Wraps the Shopify Cart API and re-renders the drawer using the
 * Section Rendering API (?section_id=cart-drawer). Falls back to
 * the native form POST on failure so JS-disabled clients still
 * reach /cart.
 */
(function () {
  'use strict';

  const SECTION_ID = 'cart-drawer';
  const TOAST_TIMEOUT = 4000;

  const selectors = {
    drawer: '[data-gg-cart-drawer]',
    backdrop: '[data-gg-cart-backdrop]',
    toggle: '[data-gg-cart-toggle]',
    close: '[data-gg-cart-close]',
    addForm: 'form[action$="/cart/add"]',
    removeForm: 'form[data-gg-cart-remove]',
    lineQty: '[data-gg-cart-line-qty]',
    lineDisplay: '[data-gg-cart-line-display]',
    increase: '[data-gg-cart-increase]',
    decrease: '[data-gg-cart-decrease]',
    countBadge: '[data-gg-cart-count]',
  };

  // Drop overlapping mutations to avoid racing refreshDrawer() — taps during a pending op are ignored.
  let pending = false;
  async function withPending(fn) {
    if (pending) return;
    pending = true;
    try {
      await fn();
    } finally {
      pending = false;
    }
  }

  function toast(message, tone = 'info') {
    const root = document.getElementById('gg-toast-root') || createToastRoot();
    const el = document.createElement('div');
    el.className =
      'gg-toast font-label text-xs tracking-widest uppercase px-4 py-3 mb-2 ' +
      (tone === 'error' ? 'bg-error text-on-error' : 'bg-primary text-on-primary');
    el.setAttribute('role', tone === 'error' ? 'alert' : 'status');
    el.textContent = message;
    root.appendChild(el);
    setTimeout(() => el.remove(), TOAST_TIMEOUT);
  }

  function createToastRoot() {
    const r = document.createElement('div');
    r.id = 'gg-toast-root';
    r.className = 'fixed bottom-6 right-6 z-[100] flex flex-col items-end pointer-events-none';
    r.setAttribute('aria-live', 'polite');
    document.body.appendChild(r);
    return r;
  }

  // Trigger that opened the drawer — restored on close so keyboard users
  // return to where they were instead of the top of the page.
  let lastTrigger = null;

  function focusableIn(drawer) {
    return Array.from(
      drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => el.offsetParent !== null);
  }

  function openDrawer() {
    const drawer = document.querySelector(selectors.drawer);
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'false');
    drawer.classList.add('is-open');
    document.documentElement.classList.add('gg-cart-open');
    const first = focusableIn(drawer)[0];
    if (first) first.focus();
  }

  function closeDrawer() {
    const drawer = document.querySelector(selectors.drawer);
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'true');
    drawer.classList.remove('is-open');
    document.documentElement.classList.remove('gg-cart-open');
    if (lastTrigger && typeof lastTrigger.focus === 'function') {
      lastTrigger.focus();
      lastTrigger = null;
    }
  }

  async function refreshDrawer() {
    try {
      const url = new URL(window.location.pathname, window.location.origin);
      url.searchParams.set('section_id', SECTION_ID);
      const res = await fetch(url.toString(), { headers: { Accept: 'text/html' } });
      if (!res.ok) throw new Error('section render failed');
      const html = await res.text();
      const drawer = document.querySelector(selectors.drawer);
      if (!drawer) return;
      const next = document.createElement('div');
      next.innerHTML = html.trim();
      const newRoot = next.querySelector(selectors.drawer);
      const wasOpen = drawer.classList.contains('is-open');
      if (newRoot) {
        // Preserve open state across the swap so users don't see the
        // drawer flicker closed when a section render is triggered
        // while the drawer is visible.
        if (wasOpen) {
          newRoot.classList.add('is-open');
          newRoot.setAttribute('aria-hidden', 'false');
        }
        drawer.replaceWith(newRoot);
      } else {
        drawer.innerHTML = next.innerHTML;
      }
      bindDrawer();
      if (window.GG_AccountCTA && typeof window.GG_AccountCTA.init === 'function') {
        window.GG_AccountCTA.init();
      }
    } catch (err) {
      console.error('[gg-cart] drawer refresh failed:', err);
    }
  }

  async function refreshCount() {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      document.querySelectorAll(selectors.countBadge).forEach((el) => {
        el.textContent = cart.item_count;
        el.dataset.count = cart.item_count;
      });
    } catch (err) {
      /* ignore */
    }
  }

  async function postJson(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.description || data.message || 'cart request failed');
    }
    return res.json();
  }

  async function addLine({ id, quantity = 1, sellingPlan }) {
    const body = { items: [{ id, quantity }] };
    if (sellingPlan) body.items[0].selling_plan = sellingPlan;
    return postJson('/cart/add.js', body);
  }

  async function changeLine({ line, quantity }) {
    return postJson('/cart/change.js', { line, quantity });
  }

  function bindDrawer() {
    document.querySelectorAll(selectors.removeForm).forEach((form) => {
      form.addEventListener('submit', onRemoveSubmit);
    });
    document.querySelectorAll(selectors.increase).forEach((btn) => {
      btn.addEventListener('click', onQtyDelta);
    });
    document.querySelectorAll(selectors.decrease).forEach((btn) => {
      btn.addEventListener('click', onQtyDelta);
    });
  }

  async function onRemoveSubmit(e) {
    e.preventDefault();
    const form = e.currentTarget;
    const line = parseInt(form.dataset.ggCartLine || '0', 10);
    if (!line) return;
    await withPending(async () => {
      try {
        await changeLine({ line, quantity: 0 });
        await Promise.all([refreshDrawer(), refreshCount()]);
      } catch (err) {
        toast(err.message || 'Could not remove item', 'error');
        form.submit();
      }
    });
  }

  async function onQtyDelta(e) {
    e.preventDefault();
    const btn = e.currentTarget;
    const line = parseInt(btn.dataset.ggCartLine || '0', 10);
    const delta = btn.matches(selectors.increase) ? 1 : -1;
    const current = parseInt(btn.dataset.ggCartLineQty || '1', 10);
    const next = Math.max(0, current + delta);
    if (!line) return;
    await withPending(async () => {
      try {
        await changeLine({ line, quantity: next });
        await Promise.all([refreshDrawer(), refreshCount()]);
      } catch (err) {
        toast(err.message || 'Could not update quantity', 'error');
      }
    });
  }

  async function onAddSubmit(e) {
    const form = e.target.closest(selectors.addForm);
    if (!form) return;
    if (form.dataset.ggCartNoAjax === 'true') return;
    e.preventDefault();
    const formData = new FormData(form);
    const id = formData.get('id');
    const quantity = parseInt(formData.get('quantity') || '1', 10);
    const sellingPlan = formData.get('selling_plan');
    await withPending(async () => {
      try {
        await addLine({ id, quantity, sellingPlan });
        await Promise.all([refreshDrawer(), refreshCount()]);
        openDrawer();
      } catch (err) {
        toast(err.message || 'Could not add item', 'error');
        // Native fallback to /cart
        form.submit();
      }
    });
  }

  function bindGlobal() {
    document.addEventListener('click', (e) => {
      const toggle = e.target.closest(selectors.toggle);
      if (toggle) {
        e.preventDefault();
        lastTrigger = toggle;
        openDrawer();
        refreshDrawer();
        return;
      }
      const close = e.target.closest(selectors.close);
      if (close) {
        e.preventDefault();
        closeDrawer();
        return;
      }
      const backdrop = e.target.closest(selectors.backdrop);
      if (backdrop) {
        closeDrawer();
      }
    });

    document.addEventListener('submit', onAddSubmit);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        closeDrawer();
        return;
      }
      if (e.key !== 'Tab') return;
      const drawer = document.querySelector(selectors.drawer);
      if (!drawer || !drawer.classList.contains('is-open')) return;
      const list = focusableIn(drawer);
      if (!list.length) return;
      const first = list[0];
      const last = list[list.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !drawer.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !drawer.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    bindGlobal();
    bindDrawer();
  });

  window.ggCart = {
    add: (opts) => addLine(opts).then(() => Promise.all([refreshDrawer(), refreshCount()])),
    remove: (line) =>
      changeLine({ line, quantity: 0 }).then(() => Promise.all([refreshDrawer(), refreshCount()])),
    update: (line, quantity) =>
      changeLine({ line, quantity }).then(() => Promise.all([refreshDrawer(), refreshCount()])),
    open: openDrawer,
    close: closeDrawer,
    refresh: refreshDrawer,
  };
})();
